import { Box, CardMedia, CircularProgress, Icon, InputAdornment, Typography } from '@mui/material'
import React, { useContext, useEffect, useRef, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import AdxInput from 'components/custom/AdxInput'
import { authContext } from 'contexts/AuthContext'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getInterviewers } from 'services/api/modules/recruitment/scheduler'
import EditEmailModal from './EditEmailModal'
import { updateCandidate } from 'services/api/modules/recruitment/external-talent'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'

const ParticipantPanel = ({
  interviewId,
  candidateName,
  candidateEmail,
  candidateImage,
  interviewerList,
  setInterviewerList,
  profile_type,
}) => {
  const { auth } = useContext(authContext)
  const queryClient = useQueryClient()
  const [isFocused, setIsFocused] = useState(false)
  const [interviewerAvailable, setInterviewerAvailable] = useState([])
  const userSearchRef = useRef(null)
  const [externalGuest, setExternalGuest] = useState({
    type: 'External Guest',
    id: '',
    email: '',
  })

  const emailValidation = (email) => {
    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/

    if (email.match(validRegex)) {
      return true
    } else {
      return false
    }
  }

  const isEmailValid = emailValidation(externalGuest.email)
  const [emailErrorMessage, setEmailErrorMessage] = useState(false)

  const userName = auth.data.staff.name
  const userEmail = auth.data.staff.work_email
  const userImage = auth.data.profile_image_url

  const { data: interviewerData, isLoading } = useQuery(
    'getInterviewers',
    async () => {
      const { data } = await getInterviewers()
      return data
    },
    {
      refetchOnWindowFocus: false,
    }
  )

  const {
    mutate: editDetails,
    error: updateError,
    isLoading: editDetailIsLoading,
    isSuccess: editDetailIsSuccess,
  } = useMutation((data) => updateCandidate(interviewId, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('getInterview')
      queryClient.invalidateQueries('getCandidate')
      queryClient.invalidateQueries('getAllCandidate')
      queryClient.invalidateQueries('getAllApplicant')
      queryClient.invalidateQueries('getApplicantDetail')
      // setOpen(false)
    },
    onError: (error) => {
      console.error(error)
    },
  })
  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = (event) => {
    if (!userSearchRef.current.contains(event.target)) {
      setIsFocused(false)
      setEmailErrorMessage(false)
    }
  }

  const handleChange = (event) => {
    setExternalGuest((prev) => ({
      type: prev.type,
      id: event.target.value,
      email: event.target.value,
    }))
  }

  const handleAddExternalGuest = () => {
    if (isEmailValid) {
      setEmailErrorMessage(false)
      setInterviewerList([...interviewerList, externalGuest])
      setExternalGuest((prev) => ({ type: prev.type, id: '', email: '' }))
    } else {
      setEmailErrorMessage(true)
    }
  }

  const handleAddInterviewer = (interviewer) => {
    setInterviewerList([...interviewerList, interviewer])
    const updatedAvailableList = interviewerAvailable.filter((i) => i.id !== interviewer.id)
    setInterviewerAvailable(updatedAvailableList)
  }

  const handleRemoveInterviewer = (interviewer) => {
    const updatedList = interviewerList.filter((i) => i.id !== interviewer.id)
    setInterviewerList(updatedList)
    if (interviewer.type !== 'External Guest') {
      setInterviewerAvailable([...interviewerAvailable, interviewer].sort((a, b) => a.id - b.id))
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleAddExternalGuest()
    }
    if (event.key === 'Escape') {
      setIsFocused(false)
      setEmailErrorMessage(false)
    }
  }

  useEffect(() => {
    // Attach a click event listener to the document to handle clicks outside the user list container
    document.addEventListener('click', handleBlur)

    return () => {
      // Remove the click event listener when the component unmounts
      document.removeEventListener('click', handleBlur)
    }
  }, [])

  useEffect(() => {
    if (!isLoading) {
      const updatedAvailableList =
        interviewerList.length === 0
          ? interviewerData.filter((i) => i.email !== userEmail)
          : interviewerData.filter((i) => !interviewerList.map((e) => e.email).includes(i.email))
      setInterviewerAvailable(updatedAvailableList)

      if (interviewerList.length === 0) {
        setInterviewerList([
          {
            id: interviewerData?.find((i) => i.email === userEmail).id,
            name: userName,
            email: userEmail,
            profile_photo_url: userImage,
          },
        ])
      }
    }
  }, [isLoading])

  return (
    <Box
      sx={{
        maxHeight: 'calc(100vh - 85px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        borderRight: '1px solid #E5E5E5',
        padding: '24px',
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>Candidate</Typography>
          <EditEmailModal
            candidateName={candidateName}
            candidateEmail={candidateEmail}
            candidateImage={candidateImage}
            interviewId={interviewId}
            editDetails={editDetails}
            error={updateError}
            isLoading={editDetailIsLoading}
            isSuccess={editDetailIsSuccess}
            profile_type={profile_type}
            // setOpen={setOpen}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '14px',
            backgroundColor: 'gray-background.main',
            borderRadius: '4.5px',
            paddingX: '14px',
            paddingY: '16px',
          }}
        >
          {candidateImage ? (
            <CardMedia
              component="img"
              sx={{
                width: '40px',
                height: '40px',
                borderRadius: '100%',
                objectFitL: 'cover',
              }}
              src={candidateImage}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: 'alternate.main',
                color: 'white.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '100%',
                height: '40px',
                width: '40px',
                textTransform: 'capitalize',
              }}
            >
              {candidateName.charAt(0)}
            </Box>
          )}
          <Box sx={{ width: '70%' }}>
            <Typography>{candidateName}</Typography>
            <Typography
              sx={{
                width: '100%',
                fontSize: '12px',
                fontWeight: 400,
                color: 'light-gray-text.main',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {candidateEmail}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Interviewer */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ marginBottom: '12px' }}>
          Interviewer {isLoading ? '' : `(${interviewerList.length})`}
        </Typography>
        <Box
          ref={userSearchRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            marginBottom: '8px',
          }}
        >
          <AdxInput
            onKeyDown={handleKeyDown}
            sxSubLabel={{ color: 'red' }}
            size="standard"
            placeholder="Search or type an email"
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            value={externalGuest.email}
            errorMessage={<AdxErrorMessage>Invalid email address</AdxErrorMessage>}
            isErrorShown={!isEmailValid && emailErrorMessage}
            endAdornment={
              <InputAdornment position="end">
                <Icon
                  onClick={externalGuest.email !== '' ? handleAddExternalGuest : () => {}}
                  sx={{
                    color: isFocused && 'alternate.main',
                    cursor: externalGuest.email !== '' ? 'pointer' : '',
                  }}
                >
                  add
                </Icon>
              </InputAdornment>
            }
          />
          {isFocused && (
            <Box
              sx={{
                backgroundColor: 'white.main',
                paddingY: '10px',
                position: 'absolute',
                top: '100%',
                marginTop: '7px',
                border: '1px solid #E5E5E5',
                borderRadius: '7.5px',
                boxShadow: '0px 0px 10px 4px rgba(0, 0, 0, 0.05)',
                width: '100%',
                maxHeight: '40vh',
                overflowY: 'scroll',
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    paddingY: '12px',
                  }}
                >
                  <CircularProgress color="alternate" size={20} />
                </Box>
              ) : (
                interviewerAvailable.map((i, index) => (
                  <Box
                    key={index}
                    onClick={() => handleAddInterviewer(i)}
                    sx={{
                      display: 'flex',
                      gap: '14px',
                      paddingX: '14px',
                      paddingY: '12px',
                      '&:hover': {
                        backgroundColor: 'alternate.light',
                      },
                    }}
                  >
                    {i.profile_photo_url ? (
                      <Box>
                        <CardMedia
                          component="img"
                          sx={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '100%',
                            objectFit: 'cover',
                          }}
                          src={i.profile_photo_url}
                        />
                      </Box>
                    ) : (
                      <Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: '100%',
                            backgroundColor: 'alternate.main',
                            color: 'white.main',
                            textTransform: 'capitalize',
                            width: '40px',
                            height: '40px',
                          }}
                        >
                          {i.name.charAt(0)}
                        </Box>
                      </Box>
                    )}
                    <Box>
                      <Typography>{i.name}</Typography>
                      <Typography
                        sx={{
                          width: '250px',
                          fontSize: '12px',
                          color: 'light-gray-text.main',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {i.email}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
            <CircularProgress color="alternate" size={20} />
          </Box>
        ) : (
          interviewerList.map((i, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingX: '14px',
                paddingY: '12px',
              }}
            >
              <Box sx={{ display: 'flex', gap: '14px' }}>
                {i.type === 'External Guest' ? (
                  <Box
                    sx={{
                      backgroundColor: 'default-background.main',
                      color: 'alternate.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '100%',
                      height: '40px',
                      width: '40px',
                    }}
                  >
                    <Icon>mail_outlined</Icon>
                  </Box>
                ) : i.profile_photo_url ? (
                  <CardMedia
                    component="img"
                    sx={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '100%',
                      objectFitL: 'cover',
                    }}
                    src={i.profile_photo_url}
                  />
                ) : (
                  <Box
                    sx={{
                      backgroundColor: 'alternate.main',
                      color: 'white.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '100%',
                      height: '40px',
                      width: '40px',
                      textTransform: 'capitalize',
                    }}
                  >
                    {i.name ? i.name.charAt(0) : i.email.charAt(0) ?? '-'}
                  </Box>
                )}
                <Box sx={{ width: '250px' }}>
                  <Typography>
                    {i.type === 'External Guest' ? 'External guest' : i.name ?? '-'}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 400,
                      color: 'light-gray-text.main',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    {i.email}
                  </Typography>
                </Box>
              </Box>
              {index !== 0 && (
                <CloseIcon
                  onClick={() => handleRemoveInterviewer(i)}
                  sx={{ color: 'error.main', cursor: 'pointer' }}
                />
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  )
}

export default ParticipantPanel
